import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import type { FC, ReactNode } from 'react';
import { MdArrowBack } from 'react-icons/md';

/**
 * Height of the sticky site header (`layouts/dashboard/layout.tsx`). The nav
 * row pins directly under it so the two never overlap.
 */
const SITE_HEADER_HEIGHT = { xs: 64, md: 84 };

/**
 * Horizontal page gutters from `SubNavPageContainer`. The row bleeds into them
 * so it spans the full content column and scrolling content can't peek out at
 * the edges beside it.
 */
const PAGE_GUTTER = { xs: '20px', md: '30px' };

/**
 * Above in-page overlays (draft badges, card controls, the feed filter bar at
 * 20) but below the site header (1100) and sidebar (1101) — and below MUI
 * popovers/dialogs, which start at 1300.
 */
const STICKY_NAV_Z_INDEX = 30;

export interface StickyNavRowProps {
  /** Desktop content — the breadcrumb trail. Hidden below `md`. */
  children: ReactNode;
  /** Mobile back link destination. Falls back to `history.back()` when omitted. */
  backHref?: string;
  /** Plain-text name of the back destination, e.g. the parent module. */
  backLabel?: string;
}

/**
 * Navigation row for Program / Lesson pages that stays pinned under the site
 * header while the page scrolls: the breadcrumb trail on desktop, a back
 * button on mobile. Solid page-coloured background so content scrolling
 * underneath never shows through.
 *
 * Renders both variants and switches them with CSS so there's no layout flash
 * on first paint.
 */
export const StickyNavRow: FC<StickyNavRowProps> = ({ children, backHref, backLabel }) => {
  const backText = backLabel ? `Back to ${backLabel}` : 'Back';

  const backContent = (
    <>
      <MdArrowBack size={20} />
      <Typography
        component="span"
        noWrap
        sx={{ fontFamily: '"Figtree", sans-serif', fontSize: 14, fontWeight: 600, lineHeight: '20px' }}
      >
        {backText}
      </Typography>
    </>
  );

  const backSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    maxWidth: '100%',
    color: 'text.primary',
    textDecoration: 'none',
    background: 'none',
    border: 0,
    p: 0,
    cursor: 'pointer',
    font: 'inherit',
  } as const;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: SITE_HEADER_HEIGHT,
        zIndex: STICKY_NAV_Z_INDEX,
        // Matches the page background (#f4efee, set on <body> and the site header).
        bgcolor: '#f4efee',
        mx: { xs: `-${PAGE_GUTTER.xs}`, md: `-${PAGE_GUTTER.md}` },
        px: PAGE_GUTTER,
        py: { xs: 1, md: 1.5 },
        mb: { xs: 1, md: 1.5 },
      }}
    >
      {/* Mobile: back button */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', minHeight: 28 }}>
        {backHref ? (
          <Box component={Link} to={backHref} sx={backSx}>
            {backContent}
          </Box>
        ) : (
          <Box component="button" type="button" onClick={() => window.history.back()} sx={backSx}>
            {backContent}
          </Box>
        )}
      </Box>

      {/* Desktop: breadcrumb trail */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>{children}</Box>
    </Box>
  );
};
